<script setup>
import {onMounted, onUnmounted, ref} from 'vue';
import {deleteUser} from '@/services/userService';
import router from '@/router';
import LoadingComponent from '@/components/utils/LoadingComponent.vue';
import {trackScreenSize} from '@/globals/utils/getScreenSize';
import {formatDate} from '@/globals/utils/formatDate';
import MailTo from '@/components/utils/MailTo.vue';
import PhoneCall from '@/components/utils/PhoneCall.vue';
import {customToast} from '@/globals/utils/customToast';
import {useAdminStore} from '@/stores/admin';
import PaginationComponent from '@/components/utils/PaginationComponent.vue';
import FloatingButtonAction from "@/components/utils/FloatingButtonAction.vue";

const drivers = ref([]);
const loading = ref(true);
const error = ref(null);
const screenInfo = ref({ width: 0, height: 0, deviceType: 'DESKTOP' });
let removeListener;
const adminStore = useAdminStore();
const currentPage = ref(Number(router.currentRoute.value.query.page) || 1);

// Fetch drivers based on the current page
const fetchDrivers = async () => {
  loading.value = true;
  try {
    drivers.value = await adminStore.drivers(currentPage.value);
  } catch (err) {
    error.value = 'Error loading drivers.';
    console.error(err);
  } finally {
    loading.value = false;
  }
};

// Delete a driver
const deleteUserHandler = async (id) => {
  if (confirm('Are you sure you want to remove this driver?')) {
    try {
      await deleteUser(id);
      drivers.value.data = drivers.value.data.filter(driver => driver._id !== id);
      customToast('Driver deleted successfully', 'success');
    } catch (err) {
      customToast('Error deleting the driver', 'error');
    }
  }
};

// Edit a driver
const editDriver = (driver) => {
  router.push({ name: 'EditDriver', params: { id: driver._id } });
};

// Update page and fetch drivers
const handlePageChange = async (page) => {
  currentPage.value = page;
  await router.push({ query: { page } });
  await fetchDrivers();
};

onMounted(() => {
  fetchDrivers();
  removeListener = trackScreenSize((info) => {
    screenInfo.value = info;
  });
});

onUnmounted(() => {
  if (removeListener) removeListener();
});

</script>

<template>
  <FloatingButtonAction path="/drivers/create">
    <span class="material-icons mr-2">add_circle</span> Add Driver
  </FloatingButtonAction>
  <LoadingComponent v-if="loading" />
  <div v-else class="overflow-x-auto bg-white dark:bg-gray-700 rounded-lg shadow">
    <table class="min-w-full table-auto">
      <thead class="bg-gray-100 dark:bg-gray-700 pb-3">
      <tr>
        <th class="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-200">Name</th>
        <th v-if="screenInfo.deviceType === 'DESKTOP'" class="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-200">Email</th>
        <th v-if="screenInfo.deviceType === 'DESKTOP'" class="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-200">Phone</th>
        <th v-if="screenInfo.deviceType === 'DESKTOP'" class="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-200">Role</th>
        <th v-if="screenInfo.deviceType === 'DESKTOP'" class="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-200">Created At</th>
        <th v-if="screenInfo.deviceType === 'DESKTOP'" class="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-200">Updated At</th>
        <th class="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-200">Actions</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="driver in drivers.data" :key="driver._id" class="odd:bg-gray-50 even:bg-gray-100 hover:bg-gray-200 dark:odd:bg-gray-700 dark:even:bg-gray-600 dark:hover:bg-gray-500">
        <td class="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">{{ driver.name }}</td>
        <td v-if="screenInfo.deviceType === 'DESKTOP'" class="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">
          <MailTo :email="driver.email" v-if="driver.email" />
          <span v-else>Without email</span>
        </td>
        <td v-if="screenInfo.deviceType === 'DESKTOP'" class="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">
          <PhoneCall :phone="driver.phone" v-if="driver.phone" />
          <span v-else>Without phone</span>
        </td>
        <td v-if="screenInfo.deviceType === 'DESKTOP'" class="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">{{ driver.role }}</td>
        <td v-if="screenInfo.deviceType === 'DESKTOP'" class="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">{{ formatDate(driver.createdAt) }}</td>
        <td v-if="screenInfo.deviceType === 'DESKTOP'" class="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">{{ formatDate(driver.updatedAt) }}</td>
        <td class="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">
          <div class="flex items-center space-x-2">
            <button
                @click="editDriver(driver)"
                title="Edit Driver"
                class="flex items-center justify-center px-2 py-1 bg-gray-300 hover:bg-gray-400 dark:bg-gray-700 dark:hover:bg-gray-600 text-sm font-medium rounded"
            >
              <span class="material-icons-round" style="font-size: 16px;">edit</span>
            </button>
            <button
                @click="deleteUserHandler(driver._id)"
                title="Delete Driver"
                class="flex items-center justify-center px-2 py-1 bg-red-300 hover:bg-red-400 dark:bg-red-700 dark:hover:bg-red-600 text-sm font-medium rounded"
            >
              <span class="material-icons-round" style="font-size: 16px;">delete</span>
            </button>
          </div>
        </td>
      </tr>
      </tbody>
    </table>
    <PaginationComponent
        :currentPage="currentPage"
        :totalPages="drivers.totalPages"
        :onChangePage="handlePageChange"
        class="mb-3"
    />
  </div>
</template>

<style scoped>
</style>
